package com.evoapps.evoshift.viewmodels

import com.evoapps.evoshift.ui.util.dayInSentenceRes
import com.evoapps.evoshift.domain.util.AppClock
import androidx.lifecycle.ViewModel
import androidx.lifecycle.viewModelScope
import com.evoapps.evoshift.domain.model.Shift
import com.evoapps.evoshift.domain.model.User
import com.evoapps.evoshift.domain.repository.ShiftRepository
import com.evoapps.evoshift.domain.usecase.auth.GetCurrentUserUseCase
import com.evoapps.evoshift.domain.usecase.shift.ClockException
import com.evoapps.evoshift.domain.usecase.shift.ClockStatus
import com.evoapps.evoshift.location.LocationReader
import com.evoapps.evoshift.ui.util.UiText
import com.evoapps.evoshift.ui.util.monthInDateRes
import com.evoapps.evoshift.ui.util.toNote
import com.evoapps.evoshift.ui.util.toUiText
import com.evoapps.evoshift.voice.VoiceAction
import com.evoapps.evoshift.voice.VoiceAnswer
import com.evoapps.evoshift.voice.VoiceRules
import evoshift.composeapp.generated.resources.Res
import evoshift.composeapp.generated.resources.voice_break_ended
import evoshift.composeapp.generated.resources.voice_break_started
import evoshift.composeapp.generated.resources.voice_cancelled
import evoshift.composeapp.generated.resources.voice_clocked_in
import evoshift.composeapp.generated.resources.voice_clocked_out
import evoshift.composeapp.generated.resources.voice_confirm_clock_in
import evoshift.composeapp.generated.resources.voice_confirm_clock_out
import evoshift.composeapp.generated.resources.voice_confirm_end_break
import evoshift.composeapp.generated.resources.voice_confirm_start_break
import evoshift.composeapp.generated.resources.date_day_month
import evoshift.composeapp.generated.resources.voice_free_tomorrow
import evoshift.composeapp.generated.resources.voice_next_shift
import evoshift.composeapp.generated.resources.voice_next_shift_at
import evoshift.composeapp.generated.resources.voice_no_upcoming_shift
import evoshift.composeapp.generated.resources.voice_shift_hours
import evoshift.composeapp.generated.resources.voice_shift_hours_at
import evoshift.composeapp.generated.resources.voice_shifts_and
import evoshift.composeapp.generated.resources.voice_signed_out
import evoshift.composeapp.generated.resources.voice_today
import evoshift.composeapp.generated.resources.voice_tomorrow
import evoshift.composeapp.generated.resources.voice_work_tomorrow
import evoshift.composeapp.generated.resources.voice_working_now
import evoshift.composeapp.generated.resources.voice_working_now_at
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.first
import kotlinx.coroutines.flow.update
import kotlinx.coroutines.launch
import kotlinx.datetime.DatePeriod
import kotlinx.datetime.LocalDate
import kotlinx.datetime.LocalDateTime
import kotlinx.datetime.TimeZone
import kotlinx.datetime.minus
import kotlinx.datetime.plus
import kotlinx.datetime.toLocalDateTime
import kotlin.time.Clock
import kotlin.time.Instant

/**
 * SH-150: the screen a voice assistant opens (evoshift://app/voice/…, docs/voice.md). For a
 * question ("next shift", "do I work tomorrow") it shows and reads out the answer from the
 * person's own shifts; for a clock action it asks to confirm first and then clocks through the
 * dashboard's [MyShiftClock] (with the location where the workplace checks it, SH-106).
 */
data class VoiceActionUiState(
    val action: VoiceAction,
    val loading: Boolean = true,
    /** The answer, the result, or why it cannot be done: shown and read out. */
    val message: UiText? = null,
    /** SH-106: done, but marked for the manager (outside the area, no location). */
    val note: UiText? = null,
    /** A clock action waiting for Confirm: the question (shown and read out). */
    val question: UiText? = null,
    val busy: Boolean = false,
    /** SH-106: the one-time explanation of the location reading is open. */
    val locationNotice: Boolean = false
)

class VoiceActionViewModel(
    action: VoiceAction,
    private val getCurrentUserUseCase: GetCurrentUserUseCase,
    private val shiftRepository: ShiftRepository,
    private val myShiftClock: MyShiftClock,
    private val now: () -> Instant = { AppClock.now() },
    private val timeZone: () -> TimeZone = { TimeZone.currentSystemDefault() }
) : ViewModel() {

    private val _uiState = MutableStateFlow(VoiceActionUiState(action))
    val uiState = _uiState.asStateFlow()

    private var status: ClockStatus = ClockStatus.Idle
    private var pendingReader: LocationReader? = null

    init {
        viewModelScope.launch { load() }
    }

    private suspend fun load() {
        val action = _uiState.value.action
        val user = getCurrentUserUseCase().first()
        if (user == null) {
            _uiState.update { it.copy(loading = false, message = UiText.Resource(Res.string.voice_signed_out)) }
            return
        }
        val nowLocal = now().toLocalDateTime(timeZone())
        val result = runCatching {
            if (action.isClock) {
                status = myShiftClock.observe(user.uid).first().clock
                val refusal = VoiceRules.refusal(action, status)
                if (refusal != null) _uiState.update { it.copy(message = ClockException(refusal).toUiText()) }
                else _uiState.update { it.copy(question = question(action, VoiceRules.shiftOf(status), user)) }
            } else {
                val since = nowLocal.date.minus(DatePeriod(days = 1)).toString()
                val shifts = shiftRepository.observeShiftsForUser(user.uid, since).first()
                val answer = if (action == VoiceAction.WORK_TOMORROW) VoiceRules.tomorrow(shifts, user.uid, nowLocal)
                else VoiceRules.nextShift(shifts, user.uid, nowLocal)
                _uiState.update { it.copy(message = answerText(answer, user, nowLocal)) }
            }
        }
        _uiState.update { state ->
            state.copy(loading = false, message = result.exceptionOrNull()?.toUiText() ?: state.message)
        }
    }

    /** "Confirm": clocks; where the workplace checks the location, its notice comes first (once per device). */
    fun confirm(readLocation: LocationReader? = null) {
        if (_uiState.value.question == null || _uiState.value.busy) return
        viewModelScope.launch {
            if (myShiftClock.needsLocationNotice(status)) {
                pendingReader = readLocation
                _uiState.update { it.copy(locationNotice = true) }
                return@launch
            }
            clock(readLocation)
        }
    }

    fun onLocationNoticeAnswered(accepted: Boolean) {
        val reader = pendingReader
        pendingReader = null
        _uiState.update { it.copy(locationNotice = false) }
        if (!accepted) return cancel()
        myShiftClock.acknowledgeLocationNotice()
        viewModelScope.launch { clock(reader) }
    }

    /** "Cancel": nothing changes. */
    fun cancel() {
        if (_uiState.value.question == null) return
        _uiState.update { it.copy(question = null, message = UiText.Resource(Res.string.voice_cancelled)) }
    }

    private suspend fun clock(readLocation: LocationReader?) {
        val action = _uiState.value.action
        val uid = getCurrentUserUseCase().first()?.uid ?: return
        _uiState.update { it.copy(busy = true) }
        val result = myShiftClock.click(status, uid, readLocation, breakButton = action.isBreak)
        _uiState.update {
            it.copy(
                busy = false,
                question = null,
                message = result.exceptionOrNull()?.toUiText() ?: UiText.Resource(doneText(action)),
                note = result.getOrNull()?.toNote()
            )
        }
    }

    private fun doneText(action: VoiceAction) = when (action) {
        VoiceAction.CLOCK_IN -> Res.string.voice_clocked_in
        VoiceAction.CLOCK_OUT -> Res.string.voice_clocked_out
        VoiceAction.START_BREAK -> Res.string.voice_break_started
        else -> Res.string.voice_break_ended
    }

    private fun question(action: VoiceAction, shift: Shift?, user: User): UiText {
        val hours = shift?.let { hours(it, user) } ?: UiText.Raw("")
        return UiText.Resource(
            when (action) {
                VoiceAction.CLOCK_IN -> Res.string.voice_confirm_clock_in
                VoiceAction.CLOCK_OUT -> Res.string.voice_confirm_clock_out
                VoiceAction.START_BREAK -> Res.string.voice_confirm_start_break
                else -> Res.string.voice_confirm_end_break
            },
            hours
        )
    }

    /** "08:00–16:00", or "08:00–16:00 at Aurora Gdańsk". */
    private fun hours(shift: Shift, user: User): UiText {
        val start = VoiceRules.time(shift.plannedTime.start)
        val end = VoiceRules.time(shift.plannedTime.end)
        val place = VoiceRules.workplaceName(shift, user)
        return if (place == null) UiText.Resource(Res.string.voice_shift_hours, start, end)
        else UiText.Resource(Res.string.voice_shift_hours_at, start, end, place)
    }

    private fun answerText(answer: VoiceAnswer, user: User, nowLocal: LocalDateTime): UiText = when (answer) {
        is VoiceAnswer.NextShift -> {
            val shift = answer.shift
            val place = VoiceRules.workplaceName(shift, user)
            val end = VoiceRules.time(shift.plannedTime.end)
            if (answer.ongoing) {
                if (place == null) UiText.Resource(Res.string.voice_working_now, end)
                else UiText.Resource(Res.string.voice_working_now_at, end, place)
            } else {
                val day = dayText(LocalDate.parse(shift.plannedTime.start.take(10)), nowLocal.date)
                val start = VoiceRules.time(shift.plannedTime.start)
                if (place == null) UiText.Resource(Res.string.voice_next_shift, day, start, end)
                else UiText.Resource(Res.string.voice_next_shift_at, day, start, end, place)
            }
        }
        VoiceAnswer.NoUpcomingShift -> UiText.Resource(Res.string.voice_no_upcoming_shift)
        is VoiceAnswer.WorkTomorrow -> UiText.Resource(Res.string.voice_work_tomorrow, joined(answer.shifts.map { hours(it, user) }))
        VoiceAnswer.FreeTomorrow -> UiText.Resource(Res.string.voice_free_tomorrow)
    }

    /** "08:00–12:00 and 18:00–22:00" (two shifts on one day are rare; more join the same way). */
    private fun joined(parts: List<UiText>): UiText =
        parts.reduce { acc, part -> UiText.Resource(Res.string.voice_shifts_and, acc, part) }

    /** "today", "tomorrow", or "Tuesday, 3 February". */
    private fun dayText(day: LocalDate, today: LocalDate): UiText = when (day) {
        today -> UiText.Resource(Res.string.voice_today)
        today.plus(DatePeriod(days = 1)) -> UiText.Resource(Res.string.voice_tomorrow)
        else -> UiText.Resource(
            Res.string.date_day_month,
            UiText.Resource(dayInSentenceRes(day.dayOfWeek)),
            day.day,
            UiText.Resource(monthInDateRes(day.month.ordinal + 1))
        )
    }
}
